import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { baseNewVerificationUrl } from 'packages/verifications/ui/VerificationsTemplate';
import { isVerificationPipelineRunId } from 'packages/verifications/ui/VerificationTableSharedComponents';
import { VerificationWorkflow } from 'packages/verifications/ui/VerificationWorkflow';

// Types
const RouteParamsStruct = z.object({
  artifactDocumentId: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
  creditFacilityId: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
  reportingDate: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
  fileVersion: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
});

// Exports
export const VerificationWorkflowRoute = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const {
    artifactDocumentId,
    creditFacilityId,
    reportingDate,
    fileVersion: fileVersionString = '',
  } = RouteParamsStruct.parse(params);

  let routeLevelError = false;

  const { data: userData, loading: userLoading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const fileVersion = Number.parseInt(fileVersionString, 10);

  const pipelineRunId = useMemo(() => {
    const pipelineRunParams = {
      artifactDocumentId,
      creditFacilityId,
      reportingDate,
      fileVersion,
    };
    return isVerificationPipelineRunId(pipelineRunParams)
      ? pipelineRunParams
      : undefined;
  }, [artifactDocumentId, creditFacilityId, reportingDate, fileVersion]);

  if (!pipelineRunId && location.pathname !== baseNewVerificationUrl) {
    navigate(baseNewVerificationUrl);
  }

  if (!userData || !userData.user || userLoading) {
    return <Skeleton data-testid="loading-new-verification-route" />;
  }
  const {
    user: { organizationId },
  } = userData;

  return (
    <Box padding={dashboardRoutePadding}>
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load new verification route`;
            routeLevelError = true;
            toast(errorToastConfig);
            navigate('/');
          }
        }}
      >
        <VerificationWorkflow
          organizationId={organizationId}
          pipelineRunId={pipelineRunId}
        />
      </Sentry.ErrorBoundary>
    </Box>
  );
};
